import type { User } from '../models/data';
import type { EffectivePermissionsResponse } from './userAccessApiService';

export const SCHEDULER_OFFLINE_ENABLED = (import.meta as any).env?.VITE_SCHEDULER_OFFLINE_ENABLED === 'true';
export const OFFLINE_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
let authorityEpoch = 0;
export const offlineAuthorityEpoch = () => authorityEpoch;
const PREFIX = 'mintleaf.scheduler.offline.v1:';
type Actor = Pick<User, 'id'> & Partial<User>;
export const SCHEDULER_AUTHORITY_CHANGED_EVENT = 'mintleaf:scheduler:authority-changed';
const changedUsers = new Set<string>();
export const latestSchedulerAuthorityChangedUser = () => [...changedUsers].at(-1) || null;
export const hasSchedulerAuthorityChanged = (userId: string) => changedUsers.has(userId);
export function notifySchedulerAuthorityChanged(user: Actor): void {
  changedUsers.add(user.id);
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function')
    window.dispatchEvent(Object.assign(new Event(SCHEDULER_AUTHORITY_CHANGED_EVENT), { userId: user.id }));
}
export function permissionWasReduced(previous: EffectivePermissionsResponse | undefined, next: EffectivePermissionsResponse): boolean {
  return Boolean(previous && (['schedule.read', 'schedule.write'].some(permission =>
    previous.effectivePermissions[permission] === 'allow' && next.effectivePermissions?.[permission] === 'deny') ||
    previous.capabilities?.schedulerPublish?.publishSchedule === true && next.capabilities?.schedulerPublish?.publishSchedule === false));
}
// Only an explicit, complete decision can replace confirmed local authority.
export function assertSchedulerPermissionResponse(response: EffectivePermissionsResponse): void {
  const read = response?.effectivePermissions?.['schedule.read'];
  const write = response?.effectivePermissions?.['schedule.write'];
  if (read !== 'deny' && (read !== 'allow' || !['allow','deny'].includes(write)))
    throw new Error('SCHEDULER_PERMISSION_RESPONSE_INCOMPLETE');
}
export const isSchedulerOperationRejection = (error: { statusCode?: number; message?: string }) =>
  error.statusCode === 403 && /^(DRAFT_ENTRY_READONLY|DRAFT_STAFF_DIVISION_MISMATCH|DRAFT_SHARED_LEADERSHIP_INELIGIBLE|DRAFT_STAFF_OUTSIDE_UNIT)$/.test(error.message || '');
export const offlineActorKey = (user: Actor | null | undefined): string => user?.id && user.authUid ? JSON.stringify([
  (import.meta as any).env?.VITE_BACKEND_BASE_URL || (import.meta as any).env?.VITE_FUNCTIONS_BASE_URL || 'https://mintleaf-foglalasok-4-ai-copy.onrender.com',
  user.id, user.authUid, user.accessProfileVersion ?? 0, user.bindingGeneration ?? null,
  user.authorityGeneration ?? null, user.schedulerAuthorityVersion ?? null, user.role ?? null,
  [...(user.unitIds || [])].sort(),
]) : '';
// Durable work belongs to a principal, independently of permission versions.
// Authority grants remain bound to the complete versioned actor above.
export function offlineWorkActorKey(actor: string): string {
  try { const parts = JSON.parse(actor); return Array.isArray(parts) && parts.length >= 3 && parts.slice(0,3).every(v => typeof v === 'string' && v) ? JSON.stringify(parts.slice(0,3)) : actor; } catch { return actor; }
}
export const offlineKey = (kind: string, actor: string, scope: unknown) => PREFIX + JSON.stringify([kind, actor, scope]);
export function readOffline<T>(key: string): T | null {
  try { const raw = window.localStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
// Never acknowledge a memory fallback as an offline save.
export function writeOffline(key: string, value: unknown): void {
  try { const raw = JSON.stringify(value); window.localStorage.setItem(key, raw);
    if (window.localStorage.getItem(key) !== raw) throw new Error('storage');
  } catch { throw new Error('A tartós helyi mentés nem érhető el. A művelet nem lett elmentve.'); }
}
export function removeOffline(key: string): void { try { window.localStorage.removeItem(key); } catch { /* no authority is granted on read failure */ } }
export function offlineRecords<T>(kind: string, actor: string): Array<{ key: string; value: T }> {
  const records: Array<{ key: string; value: T }> = [];
  try { for (let i = 0; i < window.localStorage.length; i++) { const key = window.localStorage.key(i);
    if (!key?.startsWith(PREFIX)) continue;
    const parts = JSON.parse(key.slice(PREFIX.length));
    const sameOwner = parts[1] === actor || (kind === 'weekly' || kind === 'conflict-backup') && offlineWorkActorKey(parts[1]) === offlineWorkActorKey(actor);
    if (parts[0] === kind && sameOwner) { const value = readOffline<T>(key); if (value) records.push({ key, value }); }
  } } catch { return []; }
  return records;
}
export type OfflineGrant = { actor: string; unitId: string; divisionId: string | null; validatedAt: number; response: EffectivePermissionsResponse };
export const authorityKey = (actor: string, unitId: string, divisionId?: string | null) => offlineKey('authority', actor, [unitId, divisionId || null]);
export function rememberOfflineGrant(user: Actor, response: EffectivePermissionsResponse, divisionId?: string | null, now = Date.now()): void {
  const actor = offlineActorKey(user); const unitId = response.unitId;
  if (!actor || !unitId || response.userId !== user.id || response.divisionId !== (divisionId || undefined)) return;
  assertSchedulerPermissionResponse(response);
  const key = authorityKey(actor, unitId, divisionId);
  const historyKey = offlineKey('authority-history', offlineWorkActorKey(actor), [unitId, divisionId || null]);
  const previous = readOffline<OfflineGrant>(key)?.response || readOffline<OfflineGrant>(historyKey)?.response;
  if (permissionWasReduced(previous, response)) notifySchedulerAuthorityChanged(user);
  removeOffline(historyKey);
  if (response.effectivePermissions['schedule.read'] !== 'allow') { removeOffline(key); return; }
  writeOffline(key, { actor, unitId, divisionId: divisionId || null, validatedAt: now, response } satisfies OfflineGrant);
  if (typeof window.dispatchEvent === 'function') window.dispatchEvent(new Event('mintleaf:offline-authority')); 
}
export function readOfflineGrant(user: Actor | null | undefined, unitId: string, divisionId?: string | null, now = Date.now()): OfflineGrant | null {
  const actor = offlineActorKey(user); if (!actor) return null;
  const grant = readOffline<OfflineGrant>(authorityKey(actor, unitId, divisionId));
  if (!grant || !grant.response?.effectivePermissions || grant.actor !== actor || grant.unitId !== unitId || grant.divisionId !== (divisionId || null) ||
      !Number.isFinite(grant.validatedAt) || now < grant.validatedAt ||
      grant.response.userId !== user?.id || grant.response.unitId !== unitId || (grant.response.divisionId || null) !== (divisionId || null) || grant.response.effectivePermissions['schedule.read'] !== 'allow') return null;
  return grant;
}
export function revokeOfflineGrants(user: Actor, observedRevocation = false): void {
  const actor = offlineActorKey(user); for (const row of offlineRecords('authority', actor)) removeOffline(row.key);
  if (observedRevocation) { authorityEpoch += 1; notifySchedulerAuthorityChanged(user); }
}
// A newly loaded authoritative profile may supersede the actor saved days ago.
// Old records are used only to detect change, never to authorize the new actor.
export function invalidateChangedOfflineActor(user: Actor): void {
  const actor = offlineActorKey(user);
  if (!actor || typeof window === 'undefined') return;
  let changed = false;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith(PREFIX)) continue;
      const parts = JSON.parse(key.slice(PREFIX.length));
      if (parts[0] !== 'authority' || parts[1] === actor) continue;
      const grant = readOffline<OfflineGrant>(key);
      const oldActor = JSON.parse(parts[1]);
      if (grant?.response.userId === user.id && oldActor[2] === user.authUid) {
        writeOffline(offlineKey('authority-history', offlineWorkActorKey(parts[1]), [grant.unitId, grant.divisionId]), grant);
        keys.push(key); changed = true;
      }
    }
    keys.forEach(removeOffline);
  } catch { return; }
  // Version changes retire old grants; current effective permissions decide
  // whether access was actually reduced. They are not revocation evidence.
  if (changed) authorityEpoch += 1;
}
export function retireOfflineActor(user: Actor): void {
  for (const { value } of offlineRecords<OfflineGrant>('authority', offlineActorKey(user))) {
    try { writeOffline(offlineKey('authority-history', offlineWorkActorKey(value.actor), [value.unitId, value.divisionId]), value); } catch { /* no new authority is granted */ }
  }
  authorityEpoch += 1; revokeOfflineGrants(user);
}
export function offlineCanPublish(user: Actor, unitId: string, divisionId?: string | null): boolean {
  const cap = readOfflineGrant(user, unitId, divisionId)?.response.capabilities?.schedulerPublish;
  return !changedUsers.has(user.id) && cap?.publishSchedule === true && cap.authUid === user.authUid && cap.applicationUserId === user.id && cap.unitId === unitId &&
    cap.accessProfileVersion === (user.accessProfileVersion ?? 0) &&
    (user.bindingGeneration === undefined || cap.bindingGeneration === user.bindingGeneration) &&
    (user.authorityGeneration === undefined || cap.authorityGeneration === user.authorityGeneration);
}
export function clearAllOfflineAuthority(): void {
  authorityEpoch += 1;
  changedUsers.clear();
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') window.dispatchEvent(new Event(SCHEDULER_AUTHORITY_CHANGED_EVENT));
  try { const keys: string[] = []; for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i); if (key?.startsWith(PREFIX) && ['authority', 'authority-history'].includes(JSON.parse(key.slice(PREFIX.length))[0])) keys.push(key);
  } keys.forEach(removeOffline); } catch { /* read failure is closed */ }
}
export function hasOfflineScope(user: Actor, unitIds: readonly string[], divisionId?: string | null, permission = 'schedule.read'): boolean {
  return (permission === 'schedule.read' || !user || !changedUsers.has(user.id)) && unitIds.length > 0 && unitIds.every(id => readOfflineGrant(user, id, divisionId)?.response.effectivePermissions[permission] === 'allow');
}


